import {
  browserLocalPersistence,
  createUserWithEmailAndPassword,
  EmailAuthProvider,
  GoogleAuthProvider,
  deleteUser,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
  sendEmailVerification,
  setPersistence,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  indexedDBLocalPersistence,
  signOut as firebaseSignOut,
  updatePassword,
  updateProfile,
} from 'firebase/auth';
import { deleteDoc, setDoc } from 'firebase/firestore';
import { PublicAuthUser } from '../../types';
import {
  assertFirebaseAuth,
  getFirebaseAuth,
} from '../firebase';
import { deleteAllInvoices, deleteCustomer, getAllCustomers } from '../db';
import { AuthError, mapFirebaseAuthError } from './errors';
import {
  actionCodeSettings,
  signInUrl,
  normalizeEmail,
  readProfile,
  syncEmailVerified,
  toPublic,
  upsertEmailIndex,
  userDoc,
  type UserProfile,
} from './profile';

const GOOGLE_REDIRECT_FLAG = 'huipsov_ig_google_redirect';
const GOOGLE_OAUTH_PHASE = 'huipsov_ig_google_oauth_phase';
const INVOICES_PATH = '/tools/general/invoice-generator/';
export const GOOGLE_AUTH_PATH =
  '/tools/general/invoice-generator/auth/google/';

let googleRedirectResultPromise: Promise<PublicAuthUser | null> | null = null;

export type GoogleOAuthPhase = 'start' | 'pending' | null;

export function setGoogleOAuthPhase(phase: 'start' | 'pending'): void {
  try {
    sessionStorage.setItem(GOOGLE_OAUTH_PHASE, phase);
    sessionStorage.setItem(GOOGLE_REDIRECT_FLAG, '1');
  } catch {
    /* ignore */
  }
}

export function getGoogleOAuthPhase(): GoogleOAuthPhase {
  try {
    const phase = sessionStorage.getItem(GOOGLE_OAUTH_PHASE);
    if (phase === 'start' || phase === 'pending') return phase;
    return null;
  } catch {
    return null;
  }
}

export function clearGoogleOAuthPhase(): void {
  try {
    sessionStorage.removeItem(GOOGLE_OAUTH_PHASE);
    sessionStorage.removeItem(GOOGLE_REDIRECT_FLAG);
  } catch {
    /* ignore */
  }
}

export function markGoogleRedirectPending(): void {
  setGoogleOAuthPhase('pending');
}

export function isGoogleRedirectPending(): boolean {
  try {
    return (
      sessionStorage.getItem(GOOGLE_REDIRECT_FLAG) === '1' ||
      getGoogleOAuthPhase() === 'pending'
    );
  } catch {
    return false;
  }
}

export function consumeGoogleRedirectPending(): boolean {
  const pending = isGoogleRedirectPending();
  clearGoogleOAuthPhase();
  return pending;
}

export function invoicesHomePath(): string {
  return INVOICES_PATH;
}

export function googleAuthPath(): string {
  return GOOGLE_AUTH_PATH;
}

/** Match initializeAuth in firebase.ts so sessions survive page reload. */
async function ensureAuthPersistence(
  auth: import('firebase/auth').Auth,
): Promise<void> {
  await setPersistence(auth, indexedDBLocalPersistence).catch(() =>
    setPersistence(auth, browserLocalPersistence),
  );
}

async function ensureGoogleProfile(user: import('firebase/auth').User): Promise<PublicAuthUser> {
  const email = normalizeEmail(user.email || '');
  const display = (user.displayName || '').trim();
  const parts = display.split(/\s+/).filter(Boolean);
  const firstName = parts[0] || '';
  const lastName = parts.slice(1).join(' ') || '';
  const now = new Date().toISOString();
  const existing = await readProfile(user.uid);

  try {
    await setDoc(
      userDoc(user.uid),
      {
        email: email || existing?.email || '',
        firstName: existing?.firstName || firstName,
        lastName: existing?.lastName || lastName,
        emailVerified: true,
        createdAt: existing?.createdAt || now,
        updatedAt: now,
        provider: 'google',
      },
      { merge: true },
    );
  } catch (error) {
    console.warn('Failed to write Google user profile:', error);
  }

  if (email) await upsertEmailIndex(email, true);
  await syncEmailVerified(user);
  return toPublic(user);
}

export function hasPasswordProvider(): boolean {
  const user = getFirebaseAuth()?.currentUser;
  return Boolean(
    user?.providerData.some((p) => p.providerId === 'password'),
  );
}

async function reauthenticateWithPassword(password: string): Promise<void> {
  const auth = assertFirebaseAuth();
  const user = auth.currentUser;
  if (!user?.email) {
    throw new AuthError('not_authenticated', 'You must be signed in');
  }
  const credential = EmailAuthProvider.credential(user.email, password);
  await reauthenticateWithCredential(user, credential);
}

export async function changePassword(input: {
  currentPassword: string;
  newPassword: string;
}): Promise<void> {
  try {
    const auth = assertFirebaseAuth();
    const user = auth.currentUser;
    if (!user) {
      throw new AuthError('not_authenticated', 'You must be signed in');
    }
    if (!hasPasswordProvider()) {
      throw new AuthError(
        'not_allowed',
        'Password change is not available for this account',
      );
    }
    await reauthenticateWithPassword(input.currentPassword);
    await updatePassword(user, input.newPassword);
  } catch (error) {
    if (error instanceof AuthError) throw error;
    throw mapFirebaseAuthError(error);
  }
}

export async function deleteAccount(input?: {
  password?: string;
}): Promise<void> {
  try {
    const auth = assertFirebaseAuth();
    const user = auth.currentUser;
    if (!user) {
      throw new AuthError('not_authenticated', 'You must be signed in');
    }

    const uid = user.uid;
    const email = normalizeEmail(user.email || '');
    const passwordUser = hasPasswordProvider();

    if (passwordUser) {
      const password = (input?.password || '').trim();
      if (!password) {
        throw new AuthError(
          'invalid_credentials',
          'Current password is required',
        );
      }
      await reauthenticateWithPassword(password);
    } else {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: 'select_account' });
      await reauthenticateWithPopup(user, provider);
    }

    await deleteAllInvoices(uid);
    const customers = await getAllCustomers(uid);
    await Promise.all(customers.map((c) => deleteCustomer(c.id, uid)));

    try {
      await deleteDoc(userDoc(uid));
    } catch (error) {
      console.warn('Failed to delete user profile:', error);
    }

    if (email) {
      try {
        await upsertEmailIndex(email, false);
      } catch {
        /* ignore */
      }
    }

    await deleteUser(user);
  } catch (error) {
    if (error instanceof AuthError) throw error;
    throw mapFirebaseAuthError(error);
  }
}

export async function signUp(input: {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
}): Promise<PublicAuthUser> {
  try {
    const auth = assertFirebaseAuth();
    const email = normalizeEmail(input.email);
    const firstName = input.firstName.trim();
    const lastName = input.lastName.trim();

    const cred = await createUserWithEmailAndPassword(auth, email, input.password);
    await updateProfile(cred.user, {
      displayName: `${firstName} ${lastName}`.trim(),
    });

    const now = new Date().toISOString();
    const profile: UserProfile = {
      email,
      firstName,
      lastName,
      emailVerified: false,
      createdAt: now,
      updatedAt: now,
    };

    try {
      await setDoc(userDoc(cred.user.uid), profile);
    } catch (error) {
      console.warn('Failed to write user profile (check Firestore rules):', error);
    }

    await upsertEmailIndex(email, false);

    try {
      await sendEmailVerification(
        cred.user,
        actionCodeSettings(signInUrl()),
      );
    } catch (error) {
      console.warn('Failed to send verification email:', error);
    }

    // Require explicit sign-in after verification
    await firebaseSignOut(auth);
    return {
      id: cred.user.uid,
      email,
      firstName,
      lastName,
      emailVerified: false,
      createdAt: now,
      updatedAt: now,
    };
  } catch (error) {
    if (error instanceof AuthError) throw error;
    throw mapFirebaseAuthError(error);
  }
}

export async function signIn(input: {
  email: string;
  password: string;
  remember: boolean;
}): Promise<PublicAuthUser> {
  try {
    const auth = assertFirebaseAuth();
    // "Remember me" saves credentials in remember-login.ts only.
    await ensureAuthPersistence(auth);
    const cred = await signInWithEmailAndPassword(
      auth,
      normalizeEmail(input.email),
      input.password,
    );

    await syncEmailVerified(cred.user);

    if (!cred.user.emailVerified) {
      await firebaseSignOut(auth);
      throw new AuthError(
        'email_not_verified',
        'Please verify your email before signing in',
      );
    }

    return toPublic(cred.user);
  } catch (error) {
    if (error instanceof AuthError) throw error;
    throw mapFirebaseAuthError(error);
  }
}

/** Google OAuth via popup. */
export async function signInWithGoogle(): Promise<PublicAuthUser> {
  try {
    const auth = assertFirebaseAuth();
    await ensureAuthPersistence(auth);

    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });
    const result = await signInWithPopup(auth, provider);
    clearGoogleOAuthPhase();
    return ensureGoogleProfile(result.user);
  } catch (error) {
    clearGoogleOAuthPhase();
    if (error instanceof AuthError) throw error;
    throw mapFirebaseAuthError(error);
  }
}

/** @deprecated Prefer signInWithGoogle (popup). Kept for /auth/google bridge. */
export async function signInWithGoogleRedirect(): Promise<void> {
  try {
    const auth = assertFirebaseAuth();
    await ensureAuthPersistence(auth);
    setGoogleOAuthPhase('pending');

    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });
    await signInWithRedirect(auth, provider);
  } catch (error) {
    clearGoogleOAuthPhase();
    if (error instanceof AuthError) throw error;
    throw mapFirebaseAuthError(error);
  }
}

/** Call once after redirect returns. Shared in-flight promise; awaits auth readiness. */
export async function completeGoogleRedirectSignIn(): Promise<PublicAuthUser | null> {
  if (!googleRedirectResultPromise) {
    googleRedirectResultPromise = (async () => {
      try {
        const auth = assertFirebaseAuth();
        await auth.authStateReady();

        const result = await getRedirectResult(auth);
        if (result?.user) {
          return ensureGoogleProfile(result.user);
        }

        if (isGoogleRedirectPending() && auth.currentUser) {
          return ensureGoogleProfile(auth.currentUser);
        }

        return null;
      } catch (error) {
        googleRedirectResultPromise = null;
        if (error instanceof AuthError) throw error;
        throw mapFirebaseAuthError(error);
      }
    })();
  }

  return googleRedirectResultPromise;
}

/**
 * Finalize Google redirect after return from the provider.
 * Only clears the pending flag when a user is resolved (or auth is ready with no user).
 */
export async function finalizeGoogleRedirectIfNeeded(): Promise<PublicAuthUser | null> {
  const pending = isGoogleRedirectPending();
  try {
    const auth = assertFirebaseAuth();
    await auth.authStateReady();

    const user = await completeGoogleRedirectSignIn();
    if (user) {
      consumeGoogleRedirectPending();
      return user;
    }

    // Auth finished with no session — clear stale pending flag.
    if (pending && !auth.currentUser) {
      consumeGoogleRedirectPending();
    }

    return null;
  } catch (error) {
    throw error;
  }
}

export async function signOut(): Promise<void> {
  const auth = getFirebaseAuth();
  if (auth) await firebaseSignOut(auth);
}

export async function getCurrentUser(): Promise<PublicAuthUser | null> {
  const auth = getFirebaseAuth();
  if (!auth) return null;
  await auth.authStateReady();
  const user = auth.currentUser;
  if (!user) return null;
  if (!user.emailVerified) {
    await firebaseSignOut(auth);
    return null;
  }
  return toPublic(user);
}

export async function updateUserProfile(input: {
  firstName: string;
  lastName: string;
}): Promise<PublicAuthUser> {
  try {
    const auth = assertFirebaseAuth();
    const user = auth.currentUser;
    if (!user) {
      throw new AuthError('not_authenticated', 'You must be signed in');
    }

    const firstName = input.firstName.trim();
    const lastName = input.lastName.trim();
    const now = new Date().toISOString();
    const existing = await readProfile(user.uid);

    await updateProfile(user, {
      displayName: `${firstName} ${lastName}`.trim(),
    });

    await setDoc(
      userDoc(user.uid),
      {
        email: user.email || existing?.email || '',
        firstName,
        lastName,
        emailVerified: existing?.emailVerified ?? user.emailVerified,
        createdAt: existing?.createdAt || now,
        updatedAt: now,
      } satisfies UserProfile,
      { merge: true },
    );

    return toPublic(user);
  } catch (error) {
    if (error instanceof AuthError) throw error;
    throw mapFirebaseAuthError(error);
  }
}
